import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/adminApi";
import { fmtDateTime } from "../lib/format";
import { Search, Filter, Loader2, ScrollText } from "lucide-react";

export default function AuditLogPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  const [actionFilter, setActionFilter] = useState("");
  const [targetIdFilter, setTargetIdFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (actionFilter) params.action = actionFilter;
      if (targetIdFilter.trim()) params.target_id = targetIdFilter.trim();
      const res = await api.get("/audit-log", { params });
      setRows(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [page, actionFilter, targetIdFilter]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto glass-animate" data-testid="audit-log-page">
      <div className="flex items-center gap-2 mb-1">
        <ScrollText className="w-5 h-5 text-[#7C5CFF]" />
        <h1 className="text-2xl font-extrabold text-[#0B0F1A] tracking-tight">Audit Log</h1>
      </div>
      <p className="text-sm text-[#5B617A] mb-5">{total} total event{total !== 1 ? "s" : ""}</p>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <select
          data-testid="audit-action-filter"
          value={actionFilter}
          onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
          className="h-9 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 text-sm text-[#0B0F1A] focus:outline-none focus:border-[#7C5CFF] backdrop-blur-sm"
        >
          <option value="">All actions</option>
          <option value="admin_login">admin_login</option>
          <option value="admin_logout">admin_logout</option>
          <option value="tenant_suspend">tenant_suspend</option>
          <option value="tenant_activate">tenant_activate</option>
          <option value="tenant_set_plan">tenant_set_plan</option>
          <option value="impersonation_start">impersonation_start</option>
          <option value="impersonation_read">impersonation_read</option>
        </select>
        <div className="flex items-center gap-2 h-9 bg-white/60 border border-[#7C5CFF]/[0.12] rounded-xl px-3 min-w-[180px] backdrop-blur-sm">
          <Filter className="w-3.5 h-3.5 text-[#5B617A] shrink-0" />
          <input
            data-testid="audit-target-filter"
            className="flex-1 bg-transparent text-sm text-[#0B0F1A] placeholder:text-[#5B617A]/50 focus:outline-none"
            placeholder="Filter by target ID…"
            value={targetIdFilter}
            onChange={(e) => setTargetIdFilter(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
          />
        </div>
      </div>

      {/* Table */}
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead>
              <tr className="bg-white/40 border-b border-[#7C5CFF]/[0.08]">
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Timestamp</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Admin</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Action</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Target</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">Before</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">After</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-[#5B617A] uppercase tracking-wider">IP</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#7C5CFF] mx-auto" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-[#5B617A]">No audit events found.</td></tr>
              ) : rows.map((r, i) => (
                <tr key={r.id || i} className="border-b border-[#7C5CFF]/[0.04] glass-row-hover transition-colors" data-testid={`audit-row-${i}`}>
                  <td className="px-3 py-2.5 text-[10px] text-[#5B617A] whitespace-nowrap">{fmtDateTime(r.timestamp)}</td>
                  <td className="px-3 py-2.5 text-xs text-[#0B0F1A] truncate max-w-[120px]">{r.admin_email}</td>
                  <td className="px-3 py-2.5">
                    <span className="inline-block bg-[#7C5CFF]/[0.08] text-[#5B617A] text-[10px] px-1.5 py-0.5 rounded font-mono whitespace-nowrap">{r.action}</span>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-[#0B0F1A]">
                    <span className="text-[10px] text-[#5B617A]">{r.target_type}:</span>{" "}
                    {r.target_name || r.target_id}
                  </td>
                  <td className="px-3 py-2.5 text-[10px] text-[#5B617A] max-w-[150px] truncate font-mono">{r.before ? JSON.stringify(r.before) : "—"}</td>
                  <td className="px-3 py-2.5 text-[10px] text-[#5B617A] max-w-[150px] truncate font-mono">{r.after ? JSON.stringify(r.after) : "—"}</td>
                  <td className="px-3 py-2.5 text-[10px] text-[#5B617A] font-mono">{r.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#7C5CFF]/[0.06]">
            <p className="text-xs text-[#5B617A]">Page {page} of {totalPages}</p>
            <div className="flex gap-1.5">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="audit-prev"
                className="px-3 py-1 rounded-lg text-xs font-semibold text-[#0B0F1A] bg-white/60 border border-[#7C5CFF]/[0.12] hover:bg-[#7C5CFF]/[0.06] disabled:opacity-40">Prev</button>
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="audit-next"
                className="px-3 py-1 rounded-lg text-xs font-semibold text-[#0B0F1A] bg-white/60 border border-[#7C5CFF]/[0.12] hover:bg-[#7C5CFF]/[0.06] disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
